'use client'

import { getValidatedAccessToken } from '@/lib/supabase-validated-session'
import { santaChatAudioExtension, type SantaChatMessage, type SantaChatTier, type SantaGesture } from '@/server/domain/santa-chat'

/** Un mensaje tal y como llega por JSON (la fecha viaja como cadena). */
export type SantaChatMessageDto = Omit<SantaChatMessage, 'createdAt'> & { createdAt: string }

export interface SantaChatOpeningDto {
  history: SantaChatMessageDto[]
  /** `tailAudioUrl`: el mp3 enlatado de la cola del saludo, que suena tras la voz en vivo. */
  greeting: ({ message: SantaChatMessageDto } & SantaChatSpeechDto) | null
  remainingToday: number
  limitReached: boolean
  /** ISO instant at which the daily limit resets; `null` while messages remain. */
  limitResetsAt: string | null
  /** `free` until the account pays once: at the limit, the chat offers the packs. */
  tier: SantaChatTier
}

/** A stored exchange: both messages were persisted. */
export interface SantaChatReplyTurnDto {
  kind: 'reply'
  childMessage: SantaChatMessageDto
  santaMessage: SantaChatMessageDto
  audioBase64: string
  remainingToday: number
  limitReached: boolean
  /** ISO instant at which the daily limit resets; `null` while messages remain. */
  limitResetsAt: string | null
  showCallCta: boolean
  tier: SantaChatTier
  /** Santa's reply is the safety answer (seekAdult): no call invitation may stay next to it. */
  safety: boolean
}

/** Nothing was stored (didNotHear / elvesError): Santa's words are a transient notice. */
export interface SantaChatFallbackTurnDto {
  kind: 'fallback'
  /** Solo un fallo de los elfos merece reenviar lo mismo; un «no te he oído», no. */
  reason: 'didNotHear' | 'elvesError'
  text: string
  gesture: SantaGesture
  audioBase64: string
  remainingToday: number
}

export type SantaChatTurnDto = SantaChatReplyTurnDto | SantaChatFallbackTurnDto

/** Fallo de la API del chat con su `code` de dominio (y `Retry-After` en los 429). */
export class SantaChatApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly retryAfterSeconds: number | null
  ) {
    super(code)
    this.name = 'SantaChatApiError'
  }
}

interface ChildScope {
  childId: string
  timeZone: string
}

const NETWORK_ERROR = 'NETWORK_ERROR'
/** Wait when a RATE_LIMITED arrives without `Retry-After`. */
const DEFAULT_REST_SECONDS = 60

/** ISO instant `seconds` after `now`. */
export function instantAfter(seconds: number, now: Date): string {
  return new Date(now.getTime() + seconds * 1000).toISOString()
}

/**
 * Policy for a RATE_LIMITED (sending or replaying): Santa "rests" until the
 * instant the server asked for. `null` when the error is anything else.
 */
export function restUntilFrom(error: unknown, now: Date): string | null {
  if (!(error instanceof SantaChatApiError) || error.code !== 'RATE_LIMITED') return null
  return instantAfter(error.retryAfterSeconds ?? DEFAULT_REST_SECONDS, now)
}

function parseRetryAfter(response: Response): number | null {
  const seconds = Number(response.headers.get('Retry-After'))
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null
}

async function toApiError(response: Response): Promise<SantaChatApiError> {
  const body: unknown = await response.json().catch(() => null)
  const code = (body as { error?: { code?: unknown } } | null)?.error?.code
  return new SantaChatApiError(
    response.status,
    typeof code === 'string' ? code : `HTTP_${response.status}`,
    parseRetryAfter(response)
  )
}

async function authHeaders(json: boolean): Promise<Record<string, string>> {
  const token = await getValidatedAccessToken()
  if (!token) throw new SantaChatApiError(401, 'UNAUTHORIZED', null)
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
  if (json) headers['Content-Type'] = 'application/json'
  return headers
}

interface ChatRequestInit {
  body?: BodyInit
  json?: boolean
  method?: 'POST' | 'DELETE'
}

/**
 * `fetch` de las APIs del chat: sin red, `NETWORK_ERROR`; con un error de la
 * API, `SantaChatApiError` con su código de dominio y su `Retry-After`. Sin
 * sesión: el chat público la usa tal cual y el del niño le añade el token.
 */
export async function requestJson<T>(
  path: string,
  init: { body?: BodyInit; headers?: Record<string, string>; method?: 'POST' | 'DELETE' }
): Promise<T> {
  const response = await fetch(path, { method: init.method ?? 'POST', headers: init.headers, body: init.body }).catch(() => null)
  if (!response) throw new SantaChatApiError(0, NETWORK_ERROR, null)
  if (!response.ok) throw await toApiError(response)
  return (await response.json()) as T
}

/** `fetch` con el token de sesión, como el resto de clientes del panel. */
async function request<T>(path: string, init: ChatRequestInit): Promise<T> {
  return requestJson<T>(path, { ...init, headers: await authHeaders(init.json === true) })
}

function postJson<T>(path: string, body: Record<string, unknown>): Promise<T> {
  return request<T>(path, { body: JSON.stringify(body), json: true })
}

export function openSantaChat(scope: ChildScope): Promise<SantaChatOpeningDto> {
  return postJson('/api/santa-chat/open', { ...scope })
}

export function sendSantaChatText(scope: ChildScope & { text: string }): Promise<SantaChatTurnDto> {
  return postJson('/api/santa-chat/messages', { ...scope })
}

export function sendSantaChatVoice(
  scope: ChildScope & { audio: Blob; mimeType: string }
): Promise<SantaChatTurnDto> {
  const form = new FormData()
  form.set('childId', scope.childId)
  form.set('timeZone', scope.timeZone)
  const extension = santaChatAudioExtension(scope.mimeType) ?? 'webm'
  form.set('audio', new File([scope.audio], `nota.${extension}`, { type: scope.mimeType }))
  return request('/api/santa-chat/messages', { body: form })
}

/** Borra la conversación de un niño con Papá Noel. */
export function deleteSantaChatHistory(childId: string): Promise<unknown> {
  return request(`/api/santa-chat/history?childId=${encodeURIComponent(childId)}`, { method: 'DELETE' })
}

/** La voz de una nota: la parte en vivo y, en el saludo, la URL del mp3 de su cola. */
export interface SantaChatSpeechDto {
  audioBase64: string
  tailAudioUrl?: string
}

/** Vuelve a pedir la voz de un mensaje de Papá Noel (para repetirlo desde el historial). */
export function fetchSpeech(messageId: string): Promise<SantaChatSpeechDto> {
  return postJson('/api/santa-chat/speech', { messageId })
}
